/**
 * Telling apart the two `409`s that a key or a slug can answer.
 *
 * Both listings in this panel sit on a unique index that does **not** look at `deletedAt`
 * (`@@unique([tenantId, key])`, `@@unique([tenantId, slug])`), so a soft-deleted row keeps its
 * identifier reserved while the listing hides it. That is the dead end this parser exists for: the
 * BFF now answers a conflict that names the deleted row — when it is the deleted row holding it —
 * and the screen can offer to restore it instead of showing a wall of text about a resource nobody
 * can see.
 *
 * When a **live** row holds the identifier the BFF keeps the old, plain message, and this returns
 * `null`: there is nothing to restore, and "already exists" is the whole truth.
 *
 * ## Why parse a sentence
 *
 * Because that is what the contract gives. The conflict carries no structured fields, only prose,
 * and prose is what has to be read to reach the two things the screen needs: **which row** and
 * **since when**. Both are taken from the message's own restore path and ISO date rather than
 * guessed, so a message that changes shape makes this return `null` — the screen falls back to
 * showing the BFF's text, which is exactly what it did before. A wrong offer would be worse than
 * no offer.
 *
 * @module Shared/utils
 */

/** Which listing the deleted row belongs to, taken from the restore path the BFF spells out. */
export type DeletedHolderKind = 'opt-in-feature' | 'survey';

export interface DeletedHolder {
  kind: DeletedHolderKind;
  /** Id of the deleted row, for `PATCH /:id/restore`. */
  id: string;
  /** ISO-8601, as the BFF wrote it. `null` when the message carried no readable date. */
  deletedAt: string | null;
}

const RESTORE_PATH = /\/v1\/(opt-in-features|surveys)\/([A-Za-z0-9_-]+)\/restore/;
const DELETED_ON = /deleted on (\d{4}-\d{2}-\d{2}T[\d:.]+Z)/;

const KIND_BY_SEGMENT: Record<string, DeletedHolderKind> = {
  'opt-in-features': 'opt-in-feature',
  surveys: 'survey',
};

/**
 * Reads a `409` message and returns the deleted row holding the identifier, or `null`.
 *
 * `null` covers every case the screen must not act on: a live row holding it, a message in another
 * shape, an empty message. Only the restore path is required — a missing or unparseable date still
 * leaves an offer worth making, so it comes back as `null` inside the result rather than throwing
 * the whole match away.
 */
export const parseDeletedHolder = (message: string | undefined | null): DeletedHolder | null => {
  if (!message) return null;

  const path = RESTORE_PATH.exec(message);
  if (!path) return null;

  const kind = KIND_BY_SEGMENT[path[1]];
  if (!kind) return null;

  const date = DELETED_ON.exec(message);

  return { kind, id: path[2], deletedAt: date ? date[1] : null };
};
